import * as React from 'react';
import { Paper, Typography } from '@mui/material';
import { ExternshipUser } from '@api/models/careerServicesApi.models';
import { formatDate } from '@common/helpers/dateHelpers/formatDate';
import { styled } from '@mui/system';

const Styled = {
  Root: styled(Paper)(({ theme }) => ({
    padding: '1em 1em 1em 3.5em', // Increased left padding by 0.5em (from 3em to 3.5em)
    borderBottom: `1px solid ${theme.palette.GRAY_1.main}`,
    borderRadius: 0
  })),
  GridTable: styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    width: '100%',
    borderTop: '1px solid rgba(224, 224, 224, 0.4)',
    borderLeft: '1px solid rgba(224, 224, 224, 0.4)'
  }),
  GridHeader: styled('div')(({ theme }) => ({
    padding: '0.3em 0.75em',
    backgroundColor: theme.palette.GOLD_LIGHT.main,
    fontWeight: 500,
    fontSize: '1rem',
    borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
    borderRight: '1px solid rgba(200, 200, 200, 0.6)',
    display: 'flex',
    alignItems: 'center'
  })),
  GridRow: styled('div')({
    display: 'contents'
  }),
  GridCell: styled('div')({
    padding: '0.5em 0.75em',
    borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
    borderRight: '1px solid rgba(224, 224, 224, 0.4)',
    fontSize: '.9rem',
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'flex-start'
  }),
  CellText: styled(Typography)({
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 1.2
  })
};

interface Props {
  users: ExternshipUser[];
}

const CsCompaniesExpandPanel: React.FC<Props> = ({ users }) => {
  const headers = ['NAME', 'ROLE', 'EMAIL', 'PHONE', 'GRAD DATE', 'PROGRAM'];
  const usersToDisplay: ExternshipUser[] = React.useMemo(() => {
    return users.filter(user => user?.firstName || user?.lastName || user?.email || user?.phone);
  }, [users]);

  return (
    <Styled.Root>
      <Styled.GridTable role="table">
        {headers.map(header => (
          <Styled.GridHeader key={header} role="columnheader">
            <Styled.CellText variant="EC_TYPE_XS" className="info-title">
              {header}
            </Styled.CellText>
          </Styled.GridHeader>
        ))}

        {usersToDisplay.map((user) => (
          <Styled.GridRow key={user.email} role="row">
            <Styled.GridCell role="cell">
              <Styled.CellText variant="EC_TYPE_XS" className="info-text">
                {user?.firstName} {user?.lastName}
              </Styled.CellText>
            </Styled.GridCell>
            <Styled.GridCell role="cell">
              <Styled.CellText
                variant="EC_TYPE_XS"
                className="info-text"
                sx={{ textTransform: 'capitalize' }}
              >
                {user?.approverRole + (user?.isCompanyAdmin ? ' (Admin)' : '')}
              </Styled.CellText>
            </Styled.GridCell>
            <Styled.GridCell role="cell">
              <Styled.CellText variant="EC_TYPE_XS" className="info-text">
                {user?.email}
              </Styled.CellText>
            </Styled.GridCell>
            <Styled.GridCell role="cell">
              <Styled.CellText variant="EC_TYPE_XS" className="info-text">
                {user?.phone || '\u00A0'}
              </Styled.CellText>
            </Styled.GridCell>
            <Styled.GridCell role="cell">
              <Styled.CellText variant="EC_TYPE_XS" className="info-text">
                {user?.gradDate
                  ? formatDate(new Date(user.gradDate).toDateString())
                  : '\u00A0'}
              </Styled.CellText>
            </Styled.GridCell>
            <Styled.GridCell role="cell">
              <Styled.CellText variant="EC_TYPE_XS" className="info-text">
                {user?.program || '\u00A0'}
              </Styled.CellText>
            </Styled.GridCell>
          </Styled.GridRow>
        ))}
      </Styled.GridTable>
    </Styled.Root>
  );
};

export default CsCompaniesExpandPanel;
